import { getActiveDecorationSet } from "@/lib/utils/decorations";
import type { CSSProperties } from "react";
import { getDecorationSet } from "./registry";
import type { DecorationLogo, FooterArt, GameBarBackground, NavSlime } from "./types";

/** Matches the header/footer wordmark attributes used outside seasonal themes. */
export const DEFAULT_SITE_LOGO: DecorationLogo = {
  src: "/images/logos/logo-white.svg",
  width: 200,
  height: 60,
};

/** Site wordmark for the active decoration set, or the default logo. */
export function getActiveSiteLogo(): DecorationLogo {
  const setName = getActiveDecorationSet();
  if (!setName) return DEFAULT_SITE_LOGO;
  return getDecorationSet(setName)?.logo ?? DEFAULT_SITE_LOGO;
}

/** Drip tile under the nav, if the active set defines one. */
export function getActiveNavSlime(): NavSlime | null {
  const setName = getActiveDecorationSet();
  if (!setName) return null;
  return getDecorationSet(setName)?.navSlime ?? null;
}

/** Illustration under the footer logo, if the active set defines one. */
export function getActiveFooterArt(): FooterArt | null {
  const setName = getActiveDecorationSet();
  if (!setName) return null;
  return getDecorationSet(setName)?.footerArt ?? null;
}

/** In-game bar textures for the active decoration set, if that set defines any. */
export function getActiveGameBarBackground(): GameBarBackground | null {
  const setName = getActiveDecorationSet();
  if (!setName) return null;
  return getDecorationSet(setName)?.gameBarBackground ?? null;
}

/**
 * CSS variables consumed by `.decoration-game-bar`.
 * The side rail fits the vertical tile to the bar width and repeats it from the top.
 * Portrait mobile fits the horizontal tile to the bar height and repeats it from the left.
 */
export function gameBarBackgroundStyle(background: GameBarBackground): CSSProperties {
  return {
    "--game-bar-bg-vertical": `url("${background.vertical}")`,
    "--game-bar-bg-horizontal": `url("${background.horizontal}")`,
  } as CSSProperties;
}
